define((require) => {
    "use strict";

    const Configuration     = require("shared/services/Configuration");
    const {DateUtil}          = require("shared/util/DateUtil");
    const EditorEvents      = require("shared/services/EditorEvents");
    const ErrorAction       = require("editor/services/error/ErrorAction");
    const ErrorHandler      = require("shared/services/error/ErrorHandler");
    const GroupUtil         = require("ui/groups/GroupUtil");
    const ImprovedView      = require("editor/zbone/ImprovedView");
    const Ladda             = require("ladda"); // for spinner
    const LoggedIn          = require("shared/security/LoggedIn");
    const LoggedInUserState = require("shared/ui/user/LoggedInUserState");
    const {Logger}            = require("shared/util/Logger");
    const NotificationBar   = require("editor/ui/notificationbar/NotificationBar");
    const Promise           = require("shared/util/Promise");
    const PublishableState  = require("util/PublishableState");
    const Syncer            = require("shared/services/sync/Syncer");
    const template          = require("editor/ui/experience/editor/ExperienceSyncStateView.tpl");
    const Time              = require("shared/services/Time");
    const Tooltip           = require("ui/common/Tooltip");
    const User              = require("editor/ui/user/User");
    const UserCollection    = require("ui/user/UserCollection");
    const Window            = require("shared/services/Window");
    const CopyEmailButtonView  = require("ui/editor/email/CopyEmailButtonView");
    const {COPY_CODE_BUTTON_STATE, BUTTON_STATUS} = require("shared/util/copyCode");
    const Storage           = require("shared/services/Storage");
    const $                 = require("jquery");


    // Short-cut...
    const Actions = ErrorHandler.Actions;

    /**
     * View for the experience sync state in the editor. <p>
     *
     * The view consists of the sync state as text that will change according to the experience sync (save) state,
     * e.g. "Unsaved", "Saving" and "Saved".
     *
     * The view also has a tooltip with additional information, such as the actual timestamp for last edited at
     * and optional last push date if published. <p>
     *
     * The view is clickable on certain types of errors: to reload or to manually try and save the experience again.
     * Reload will be an option on for example versioning conflicts since the experience can never be saved in that
     * case. Retry is an option if we got an error deemed temporary in nature, though that may not be the case, and
     * the user can thus try a manual save. Such clicks will be tracked via StatsD as "experience.user.reload" and
     * "experience.user.save", respectively. <p>
     *
     * This view will listen for unhandled global errors and update this view accordingly based on the current
     * state of the experience; it will not report them elsewhere. <p>
     *
     * The view is the same regardless of user role, since an experience can only be saved with
     * a proper role; if not, the last edited time is merely presented.
     *
     * @class ExperienceSyncStateView
     * @constructor
     *
     * @cfg {ExperienceModel} model The experience model for the editor.
     *
     * @author Gunni Rode / <a href="http://www.zmags.com">Zmags</a>
     */
    return ImprovedView.extend({

        template,
        _isEmailInCreatorEnabled: false,
        _isEnhancedCopyCodeEnabled: false,

        /**
         * @private @type {{off:Function}}
         * Hook to remove global error handler on close.
         */
        _errorHandler: null,
        /**
         * @private @type {Number}
         * Timer to update last sync time so it "ticks".
         */
        _syncStateTimer: null,
        /**
         * @private @type {Boolean}
         * True if the experience is currently being synced (saved).
         */
        _syncing: null,
        /**
         * @private @type {String}
         * The last sync state read by this view.
         */
        _lastSyncState: null,
        /**
         * @private @type {Number}
         * Server time for last edit time in ms since the epoch.
         */
        _editedAt: null, // server time
        /**
         * @private @type {Ladda}
         * Spinner activated while a sync is pending. <p>
         *
         * By default activated, but can be disabled by setting <tt>options.spinner</tt> to false.
         */
        _spinner: null,

        modelEvents: {
            "sync:state": "_syncState" // update this view when "sync:state" is triggered
        },

        events: {
            "click @ui.getCode": "_handleGetCodeClick",
            "click @ui.syncState": "_handleSyncStateClick",
            "click @ui.getEnhancedCode": "_handleGetEnhancedCodeClick"
        },

        ui: {
            spinner: ".spinner",
            glyphicon: ".glyphicon",
            syncState: ".sync-state",
            getCode: ".get-code-btn",
            copyCodeBtn: ".copy-code-btn",
            getEnhancedCode: ".get-enhanced-code",
            getCodeIndicator: ".indicator",
            getCodePayload: ".get-code-container input[type=text]"
        },

        regions: {
            copyEmailButtonRegion: ".copy-email-btn-region"
        },

        templateHelpers: function() {
            return {
                isEmailInCreatorEnabled: this._isEmailInCreatorEnabled,
                isEnhancedCopyCodeEnabled: this._isEnhancedCopyCodeEnabled,
                isNewDashboardExperience: this.model.get("id").startsWith('C3_')
            };
        },

        /**
         * @private
         * Clears the timer used to update this view, i.e. make the "last edited" time tick.
         */
        _clearSyncTimer() {
            if (this._syncStateTimer) {
                clearTimeout(this._syncStateTimer);
                this._syncStateTimer = null;
            }
        },

        _getEnhancedCopyCodeFlag() {
            try {
                const companyId = LoggedInUserState.attributes.company.id,
                    settingName = "isEnhancedCopyCodeEnabled",
                    endpointUrl = `${Configuration.creatorSettingApiBaseUrl}/v1/settings/${companyId}?name=${settingName}`,
                    token = Storage.Local.get(Storage.Keys.LOGIN, "token") || Storage.Session.get(Storage.Keys.LOGIN, "token"),
                    bearerToken = `Bearer ${token}`;
                const options = {
                    method: "GET",
                    url: endpointUrl,
                    headers: {
                        "Authorization": bearerToken
                    },
                    success: "success",
                    dataType: "json",
                    async: false
                };

                const requestTableHTML = $.ajax(options);
                const response = requestTableHTML.responseJSON || null;
                return response.isEnhancedCopyCodeEnabled;
            } catch (error) {
                return false;
            }
        },

        _getIsCopyCodeButtonEnabled() {
            const isReflowPreventionEnabled = LoggedInUserState.get("companySettings").get("enableCNCReflowPreventionOutput");
            const copyCodeButtonState = this.model.get(COPY_CODE_BUTTON_STATE);
            if (!isReflowPreventionEnabled || !copyCodeButtonState) return true;
            return copyCodeButtonState === BUTTON_STATUS.enabled;
        },

        _getCodeIsEnabled() {
            return this._getIsCopyCodeButtonEnabled() &&
                this.model.hasAtLeastState(PublishableState.PUSHED) &&
                !!this.model.get("cncOutput") &&
                LoggedInUserState.get("integrations").isNativeContentIntegrationsEnabled() &&
                !(this.model.syncState.current.method === "push" && this._syncing);
        },

        _handleGetCodeClick() {
            if (this._getCodeIsEnabled()) {
                this.ui.getCodePayload[0].value = this.model.get("cncOutput");
                // We can use the library select for this job.
                const copyText = document.querySelector(this.ui.getCodePayload.selector);
                copyText.select();
                document.execCommand("copy");
                NotificationBar.open(`You have successfully copied this Experience snippet.`);

                const eventProperties = {
                    experienceId: this.model.get("id"),
                    experienceVersion: this.model.get("version"),
                    experiencePushedVersion: this.model.get("pushedVersion"),
                    triggerOrigin: "CopyCodeButton"
                };

                EditorEvents.trigger(EditorEvents.constants.COPY_CODE, eventProperties);
            }
        },

        _handleGetEnhancedCodeClick() {
            if (this._getCodeIsEnabled()) {
                const experienceType = LoggedInUserState.get("currentExperience").get("group") ? "groups" : "experiences";
                const id = experienceType === "groups" ? LoggedInUserState.get("currentExperience").get("group").resourceId : LoggedInUserState.get("currentExperience").get("id");
                this.ui.getCodePayload[0].value = `<script type="text/javascript" src="${process.env.APP_CONFIG.casUrl}${LoggedInUserState.get("companySettings").get("companyId")}/ssr/${experienceType}/${id}/init.js"></script>`;
                // We can use the library select for this job.
                const copyText = document.querySelector(this.ui.getCodePayload.selector);
                copyText.select();
                document.execCommand("copy");
                NotificationBar.open(`You have successfully copied this Experience snippet.`);

                const eventProperties = {
                    experienceId: this.model.get("id"),
                    experienceVersion: this.model.get("version"),
                    experiencePushedVersion: this.model.get("pushedVersion"),
                    triggerOrigin: "CopyCodeButton"
                };

                EditorEvents.trigger(EditorEvents.constants.COPY_CODE, eventProperties);
            }
        },

        _handleSyncStateClick() {
            // It is too confusing with many actions activated from here, so find one and use it...
            let action = this._action();
            if (action.indexOf(Actions.RELOAD) !== -1) {
                action = Actions.RELOAD;
            } else if (action.indexOf(Actions.RETRY) !== -1) {
                action = Actions.RETRY;
            } else if (action.indexOf(Actions.COMMUNITY) !== -1) {
                action = Actions.COMMUNITY;
            }
            ErrorAction.forModel(this.model, action)("experience");
            // Making sure the element does not act like an action element
            // as soon the action has started
            this.ui.syncState.removeClass("action");
        },

        /**
         * @private
         * Updates the sync state for this view, notified when the experience is synced *and* on uncaught errors. <p>
         *
         * Also updates this view accordingly if not closed.
         */
        _syncState() {
            this._clearSyncTimer();
            this.ui.glyphicon.hide();

            let text = "";
            let syncing = false;
            let spinner;
            let serverTime;
            const current = this.model.syncState.current;
            const state = current.state;
            const method = current.method;

            // Ignore "block" requests...
            if (method === "block") {
                return;
            }

            try {
                serverTime = Time.server();
            } catch (e) {
                NotificationBar.error({message: e.message, action: "reload"}, {closeable: false, showCloseButton: false, alert: true});
                return;
            }

            switch (state) { // current: the save going on right now, if any
                case Syncer.State.Pending:
                case Syncer.State.Syncing:
                    spinner = true;
                    this.ui.syncState.addClass("spinning");
                    syncing = true;
                    // Since we know we do not delete from the editor where this view is used...
                    text = method.indexOf("fetch") === 0 ? "Loading" : "Saving"; // also "fetchAll"
                    break;

                case Syncer.State.Unsynced:
                case Syncer.State.Synced:
                    spinner = false;
                    this.ui.syncState.removeClass("spinning");
                    text = "Saved";
                    break;

                default:
                    spinner = false;
                    this.ui.glyphicon.show();
                    this.ui.syncState.removeClass("spinning");
                    text = "Unsaved";
                    break;
            }

            this._syncing = syncing;

            this._updateGetCodeState();

            // Update every minute henceforth, assuming not saved in the meantime, meaning we get a nice
            // "Last update X minutes ago" tick effect...
            if (this._updateSyncState(state, text, spinner, serverTime)) {
                this._syncStateTimer = setTimeout(this._syncState.bind(this), 1000 * 10); // every 10 seconds fine
            }

            // Save the sync state to avoid updating tooltip again and again on same unchanged state...
            this._lastSyncState = state;
        },

        /**
         * @private
         * Updates this view based on the calculated state, reflected in the supplied text. <p>
         *
         * The text supports the tokens ${editedAt} and ${timePassed}, which will be replaced with
         * the last edited time in server time; and the time passed user friendly formatted since last update.
         *
         * @param  {String}  state       The current sync state; never null.
         * @param  {String}  text        The text to update this view with; never null.
         * @param  {Boolean} [spinner]   True to start spinner, false to stop it, undefined do leave it as-is.
         * @param  {Date}    serverTime  The server's time in milliseconds, containing information from the a call to Time.server() method
         *
         * @return {Boolean} True if the sync state is updated (if view is not closed), false otherwise.
         */
        _updateSyncState(state, text, spinner, serverTime) {
            // Only relevant when not closed, this is also called on error...
            if (this.isClosed) {
                return false;
            }

            // Make sure the user can see the view can be clicked if available for reload or manual save, but only
            // on failure state and if we have an action...
            if (state === Syncer.State.Failed && this._action()) {
                this.ui.syncState.addClass("action");
            } else {
                this.ui.syncState.removeClass("action");
            }

            let editedAt = this.model.getEditedAt(); // server time
            if (this._editedAt) {
                editedAt = this._editedAt; // server time
            }
            const timePassed = DateUtil.timeDifference(serverTime, editedAt);
            text = text.replace(/\${editedAt}/g, DateUtil.formatMMDDYYY(editedAt))
                .replace(/\${timePassed}/g, timePassed);

            this.ui.syncState.html(text);

            // Start/stop spinner?
            if (this._spinner && typeof spinner !== "undefined") {
                if (spinner) {
                    this._spinner.start();
                } else {
                    this._spinner.stop(0);
                }
            }

            // If the tooltip is currently open, we must update the contents if the state changed...
            if (state !== this._lastSyncState && Tooltip.shown(this.ui.syncState)) {
                Tooltip.show(this.ui.syncState);
            }

            return true;
        },

        /**
         * @private
         * Returns a state that dictates if it is meaningful for the user to try and do a manual save, a reload and
         * potentially lose data, or contact support (do nothing when clicking on this view).
         *
         * @return {String} The state, e.g. "reload", "retry", "community", or "support"; never null, but
         *                  will be empty on no error. Defined by <tt>ErrorHandler.Actions</tt>.
         */
        _action() {
            // Current error can be undefined if a request is pending, though an actual error may still be there...
            const error = this.model.syncState.current.error || this.model.syncState.actual.error;
            return error && error.action || "";
        },

        _updateGetCodeState() {
            this.ui.copyCodeBtn.toggleClass("disabled", !this._getCodeIsEnabled());
            if (this.model.isChangedAfterPush()) {
                this.ui.getCodeIndicator.show();
            } else {
                this.ui.getCodeIndicator.hide();
            }
        },

        _updateGetCodeTooltip() {
            const copyCodeButtonState = this._getIsCopyCodeButtonEnabled();
            this.ui.copyCodeBtn.toggleClass("disabled", !this._getCodeIsEnabled());

            const messages = {
                pushedMessage: "Click this button to copy Experience code and paste it into your eCommerce platform to publish. Remember to Push all of your changes before copying your code!",
                notPushedMessage: "Please push your Experience at least once to copy your code.",
                pushInProgressMessage: "We are still pushing your latest changes, please wait a few seconds before copying your code to get the latest version"
            };

            let messageToShow = this.model.hasAtLeastState(PublishableState.PUSHED) ? messages.pushedMessage : messages.notPushedMessage;

            if (!copyCodeButtonState) {
                messageToShow = messages.pushInProgressMessage;
            }

            return `<span>${messageToShow}</span>`;
        },

        /**
         * @private
         * Updates the tooltip for this view, automatically called after the state has changed. <p>
         *
         * Only called (via <tt>Tooltip</tt>) if <tt>this.options.showTooltip</tt> is true for this view instance.
         *
         * @return {String} The tooltip html.
         */
        _updateSyncStateTooltip() {
            const actual = this.model.syncState.actual; // we show the first error/action!
            const error = actual.error;
            const action = (error && error.action) || "";
            const pushDate = this.model.get("pushedAt");
            let html = "";

            // Add retry/reload/support information...
            if (action.indexOf(Actions.RELOAD) !== -1) {
                html += "<p><b>Click to refresh and get the saved version</b> - your changes will be lost.</p>";
            } else if (action.indexOf(Actions.RETRY) !== -1) {
                html += `<p><b>Click to try to ${actual.method} again.</b></p>`;
            }

            // Support?
            if (action.indexOf(Actions.SUPPORT) !== -1 && Configuration.supportEmail) {
                html += `<p>Contact ${Configuration.supportEmail} for assistance.</p>`;
            }

            // Add version in debug mode...
            if (Logger.level() === Logger.VERBOSE || LoggedIn.ad()) {
                html += `<p>Version: #${Math.abs(this.model.get("version"))}</p>`;
                if (pushDate) {
                    html += `<p>Pushed version: #${this.model.get("pushedVersion")}</p>`;
                }
            }

            // Add detailed timestamps...
            html += `<p>Last save: ${DateUtil.formatYYYYMMDDHHmm(this._editedAt || this.model.getEditedAt())}</p>`;
            if (pushDate) {
                html += `<p>Last push: ${DateUtil.formatYYYYMMDDHHmm(pushDate)}</p>`;
            }
            return html;
        },

        /**
         * @private
         * Shows a notification to the user about potential concurrent modification of the experience and/or one
         * of its variants, unless no interval for doing so is specified as an option or can be read from the
         * configuration based on an option. <p>
         *
         * Only called on rendering, so <tt>editedAt</tt> is server time from last refresh, unless already in
         * an unsaved state because of some earlier error. <p>
         *
         * Only shown if <tt>this.options.notifyConcurrentModification</tt> is set to a number of ms or
         * as a configuration options object returning said interval in ms ("number"), corresponding to the interval
         * passed since <tt>editedAt</tt> before the notification should *not* be shown.
         */
        _notifyConcurrentModification() {
            // Do not show if an error is already reported, or if the model is new...
            if (this.model.syncState.actual.error || this.model.isNew()) {
                return;
            }
            let interval = this.options.notifyConcurrentModification;
            // Allow the value to be configured...
            if (typeof interval !== "number") {
                interval = Configuration.val(interval, {configName: "notifyConcurrentModification", type: "number"});
            }
            // No interval (now)? Then do nothing...
            if (!interval) {
                return;
            }

            const now = Time.server();
            const updated = GroupUtil.isUpdatedWithin(this.model, this.options.group, LoggedIn.principal().get("userId"), interval, now);

            // We can stop if no concurrent updates...
            if (!updated.count) {
                return;
            }

            // See if this experience has been updated, since we want to present this as the *first* notification,
            // i.e. most relevant...
            let id = this.model.get("id");
            let queue = false;
            const experience = updated.variants[id];
            const tag = "concurrent:" + id;

            if (experience) {
                delete updated.variants[id];
                updated.count--;
                this._notifyForVariant(this.model, false, false, now, tag);
                queue = true;
            }
            // Go through all variants, then...
            if (updated.count) {
                for (id in updated.variants) {
                    this._notifyForVariant(updated.variants[id], true, queue, now, tag);
                    queue = true;
                }
            }
        },

        /**
         * @private
         * Adds a notification message for the supplied experience, which can be a variant of the actual experience
         * edited by this view.
         *
         * @param {ExperienceModel} experience The experience (variant) that could be concurrently updated; never null.
         * @param {Boolean}         variant    True if <tt>experience</tt> is a variant.
         * @param {Boolean}         queue      True to enqueue the notification message, false to show now.
         * @param {Number}          now        "Now", in ms server time since the epoch.
         */
        _notifyForVariant(experience, variant, queue, now, tag) {
            // Resolve the mysterious other editor, then, if any...
            const userId = experience.get("editedBy");
            if (!userId) {
                return;
            }
            this._fetchUser(userId, (name) => {
                // User friendly formatting of time passed...
                const timePassed = DateUtil.timeDifference(now, experience.getEditedAt());
                // eslint-disable-next-line no-template-curly-in-string
                let message = "${name} updated";

                if (variant) {
                    message += ` the Experience variant "${experience.get("name")}"`;
                } else {
                    message += " this Experience";
                }
                // eslint-disable-next-line no-template-curly-in-string
                message += " ${timePassed} ago, and <b>could</b> be editing it concurrently.";

                // Save id for message so we can remove it on successful update, since then no longer relevant. The
                // id will still be removed if the notification closes because of the delay, just not until the next
                // sync...
                const id = NotificationBar.open(message.replace(/\${name}/g, name).replace(/\${timePassed}/g, timePassed), {
                    queue:        queue,
                    closeDelay:   30000,
                    statsD:       "concurrent." + (variant ? "variant" : "experience"), // e.g. track "notificationbar.open.concurrent.variant"
                    tag:          tag, // remove all, including for variants...
                    flash:        {queue: false},
                    clearOnClose: {
                        global: "tag", manual: "tag", delay: "tag", flip: "self"
                    }
                });

                // Make sure we listen for variant saves, e.g. remove notifications on push (of all variants). Errors
                // will be caught in any case, so we do not bother about those here...
                const callback = (state, method) => {
                    // If a variant is synced, it is probably a push or a save because of a scene, but it could
                    // also be fetched. For the current exception, we are only interested in fetch, since other
                    // syncing already handled explicitly...
                    if (state === Syncer.State.Synced) {
                        // Saved, so stop listening...
                        this.stopListening(experience, "sync:state", callback);
                        // Remove the notification...
                        NotificationBar.close({id: id, clear: "self"});
                        // Re-notify on fetch (if applicable)...
                        if (method.indexOf("fetch") === 0) {
                            this._notifyConcurrentModification();
                        }
                    }
                };

                // Add listener to be invoked on synced, then no more...
                this.listenTo(experience, "sync:state", callback);
            });
        },

        /**
         * Resolves the specified user id to a user-friendly name of said user, calling <tt>notify(resolvedName)</tt>
         * when resolving is done, erroneous or not.
         *
         * @param  {String}   id     The id of the user to fetch; never null.
         * @param  {Function} notify The function to notify the user with the massage using
         *                           the resolved user name; never null.
         */
        _fetchUser(id, notify) {
            LoggedInUserState.fetchGlobalCollection(UserCollection).then((users) => {
                // Can be null if deleted, or if added to the company after the user collection was loaded...
                const user = users.findWhere({id: id});
                return user ? Promise.resolved(user) : User.findById(id);
            }).done((user) => {
                const displayName = user.get("displayName");
                const email = user.get("username");
                let name;

                if (displayName) {
                    if (email) {
                        name = `<a href="mailto:${email}">${displayName}</a>`;
                    } else {
                        name = displayName;
                    }
                } else {
                    name = `Somebody (user ${id})`;
                }
                notify(name);
            }).fail(() => { // do not report errors here, not really relevant...
                notify(`Somebody (user ${id})`);
            });
        },

        // Life cycle callbacks...

        onRender() {
            // Add tooltip if so specified...
            if (this.options.showTooltip) {
                Tooltip.add(this.ui.syncState, {
                    content:    this._updateSyncStateTooltip.bind(this),
                    html:       true,
                    placement:  "left",
                    trigger:    "hover",
                    width:      200,
                    duration:   0 // no timeout
                });
            }

            // Always hide the button
            this.ui.copyCodeBtn.hide();

            if (LoggedInUserState.get("integrations").isNativeContentIntegrationsEnabled()) {
                this.ui.copyCodeBtn.show();

                Tooltip.add(this.ui.copyCodeBtn, {
                    content:    this._updateGetCodeTooltip.bind(this),
                    html:       true,
                    placement:  "left",
                    trigger:    "hover",
                    width:      200,
                    duration:   0 // no timeout
                });
            }


            // Create spinner for dom element, not jQuery element...
            this._spinner = typeof this.options.spinner === "undefined" || this.options.spinner ? Ladda.create(this.ui.spinner[0]) : null;

            // Update sync state...
            this._syncState();

            // Notify the user that someone else is potentially modifying this experience concurrently within
            // the specified interval in ms?
            this._notifyConcurrentModification();
        },

        initialize() {
            // Global error handler which will ensure we update this view on some global unhandled error. We do not
            // report the error elsewhere, we merely update this view!
            this._errorHandler = Window.error(this._syncState.bind(this));
            this._isEmailInCreatorEnabled = LoggedInUserState.get("companySettings").get("isCreatorInEmailEnabled");
            this._isEnhancedCopyCodeEnabled = this._getEnhancedCopyCodeFlag();
        },

        onShow: function() {
            if (this._isEmailInCreatorEnabled) {
                this.copyEmailButtonRegion.show(new CopyEmailButtonView({
                    viewModel: this.options.viewModel,
                    experienceModel: this.model
                }));
            }
        },

        onClose() {
            this._clearSyncTimer();

            if (this.options.showTooltip) {
                Tooltip.remove(this.ui.syncState);
            }
            if (this._spinner) {
                this._spinner.stop();
            }
            // Close all notification messages about concurrent modification for the experience or any of
            // its variants, if not already. Note that the EditorController will probably have removed
            // them already, but we have to make sure we clean up there in case this view is used elsewhere!
            NotificationBar.close({tag: "concurrent:" + this.model.get("id"), clear: "tag"});

            if (this._errorHandler) {
                this._errorHandler.off();
                this._errorHandler = null;
            }
        }

    }, {
        injection: {
            parameters: ["statsD"],
            asOptions:  true
        }
    });
});



// WEBPACK FOOTER //
// ./src/editor/ui/experience/editor/ExperienceSyncStateView.js